import { useMutation } from '@apollo/client';
import { OUTBOUND, INBOUND } from 'constants/booking';
import { OVERRIDE_PRICE } from 'store/mutations';
import {
  isDirectFareLegs,
  isThroughFareLegs,
  isSummatedFareLegs,
  overrideSummatedFareDifferencePriceRequest
} from 'utils/summatedFaresOverridePrice';

const useSummatedFaresOverrideFareDifferenceMutation = () => {
  const [overridePrice, { loading: overridePriceLoading }] = useMutation(OVERRIDE_PRICE, { refetchQueries: ['booking'] });
  const handleMutation = async ({ variables: { pendingCancellationLegs, pendingCreationLegs, reference } }) => {
    const outboundPendingCancellationLeg = pendingCancellationLegs?.filter((leg) => leg.direction === OUTBOUND);
    const inboundPendingCancellationLeg = pendingCancellationLegs?.filter((leg) => leg.direction === INBOUND);
    const outboundPendingCreationLeg = pendingCreationLegs?.filter((leg) => leg.direction === OUTBOUND);
    const inboundPendingCreationLeg = pendingCreationLegs?.filter((leg) => leg.direction === INBOUND);
    const isExchangingOutbound = outboundPendingCancellationLeg?.length > 0 && outboundPendingCreationLeg?.length > 0;
    const isExchangingInbound = inboundPendingCancellationLeg?.length > 0 && inboundPendingCreationLeg?.length > 0;
    const isOutboundSummatedFare = isSummatedFareLegs(outboundPendingCancellationLeg);
    const isInboundSummatedFare = isSummatedFareLegs(inboundPendingCancellationLeg);
    const isOutboundThroughFare = isThroughFareLegs(outboundPendingCancellationLeg);
    const isInboundThroughFare = isThroughFareLegs(inboundPendingCancellationLeg);
    const isOutboundDirectFare = isDirectFareLegs(outboundPendingCancellationLeg);
    const isInboundDirectFare = isDirectFareLegs(inboundPendingCancellationLeg);

    if (isExchangingOutbound) {
      const isExchangingToDirectFare = isDirectFareLegs(outboundPendingCreationLeg);
      const isExchangingToThroughFare = isThroughFareLegs(outboundPendingCreationLeg);
      const isExchangingToSummatedFare = isSummatedFareLegs(outboundPendingCreationLeg);

      const priceRequest = overrideSummatedFareDifferencePriceRequest({
        cancellationLeg: outboundPendingCancellationLeg,
        createdLeg: outboundPendingCreationLeg,
        isExchangingSummatedFare: isOutboundSummatedFare,
        isExchangingDirectFare: isOutboundDirectFare,
        isExchangingThroughFare: isOutboundThroughFare,
        isExchangingToSummatedFare: isExchangingToSummatedFare,
        isExchangingToDirectFare: isExchangingToDirectFare,
        isExchangingToThroughFare: isExchangingToThroughFare
      });

      if (priceRequest.length > 0) {
        await overridePrice({
          variables: {
            reference,
            items: priceRequest
          }
        });
      }
    }

    if (isExchangingInbound) {
      const isExchangingToDirectFare = isDirectFareLegs(inboundPendingCreationLeg);
      const isExchangingToThroughFare = isThroughFareLegs(inboundPendingCreationLeg);
      const isExchangingToSummatedFare = isSummatedFareLegs(inboundPendingCreationLeg);

      const priceRequest = overrideSummatedFareDifferencePriceRequest({
        cancellationLeg: inboundPendingCancellationLeg,
        createdLeg: inboundPendingCreationLeg,
        isExchangingSummatedFare: isInboundSummatedFare,
        isExchangingDirectFare: isInboundDirectFare,
        isExchangingThroughFare: isInboundThroughFare,
        isExchangingToSummatedFare: isExchangingToSummatedFare,
        isExchangingToDirectFare: isExchangingToDirectFare,
        isExchangingToThroughFare: isExchangingToThroughFare
      });

      if (priceRequest.length > 0) {
        await overridePrice({
          variables: {
            reference,
            items: priceRequest
          }
        });
      }
    }
  };

  return [
    handleMutation,
    {
      loading: overridePriceLoading
    }
  ];
};

export default useSummatedFaresOverrideFareDifferenceMutation;
